type Distance = string | number;

const THRESHOLDS = {
  MIN_KM: 0.1,
  MIN_MILES: 0.1,
  METERS_TO_KM: 0.001,
  METERS_TO_MILES: 0.000621371,
  METERS_TO_YARDS: 1.09361,
} as const;

const formatImperial = (meters: number, includeUnit: boolean): Distance => {
  const miles = meters * THRESHOLDS.METERS_TO_MILES;

  if (miles < THRESHOLDS.MIN_MILES) {
    const yards = Math.round(meters * THRESHOLDS.METERS_TO_YARDS);
    return includeUnit ? `${yards} yd` : yards;
  }

  const roundedMiles = Math.round(miles * 10) / 10;
  return includeUnit ? `${roundedMiles} mi` : roundedMiles;
};

const formatMetric = (meters: number, includeUnit: boolean): Distance => {
  const kilometers = meters * THRESHOLDS.METERS_TO_KM;

  if (kilometers < THRESHOLDS.MIN_KM) {
    const roundedMeters = Math.round(meters);
    return includeUnit ? `${roundedMeters} m` : roundedMeters;
  }

  const roundedKm = Math.round(kilometers * 10) / 10;
  return includeUnit ? `${roundedKm} km` : roundedKm;
};

export const useImperial = (currentSite: string): boolean => {
  return /^(US|GB)$/i.test(currentSite);
};

export const formatDistance = (
  meters: number,
  currentSite: string = 'SE',
  includeUnit = true
): Distance => {
  return useImperial(currentSite)
    ? formatImperial(meters, includeUnit)
    : formatMetric(meters, includeUnit);
};

/**
 * Converts a distance in meters to kilometers or miles based on the current site.
 * @param distance - The distance in meters.
 * @param currentSite - The current site (default is 'SE').
 * @returns The distance in kilometers or miles.
 */
export const getMetersFromDistance = (
  distance: number,
  currentSite: string = 'SE'
): number => {
  return useImperial(currentSite)
    ? distance / THRESHOLDS.METERS_TO_MILES
    : distance / THRESHOLDS.METERS_TO_KM;
};
